import { FC, PropsWithChildren } from "react";
import {
  EnvelopeIcon,
  EnvelopeOpenIcon,
  HandThumbUpIcon,
  ArrowPathIcon,
  StarIcon,
  ChatBubbleLeftEllipsisIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  FireIcon,
} from "@heroicons/react/24/solid";

import { DISPOSITIONS } from "shared/lib/constants/dispositions";
import { clsxMerge } from "shared/lib/helpers";
import { PhoneRejectIcon, CloseIcon, CalendarIcon } from "shared/ui/icons";
import { NOTIFICATION_TYPES } from "shared/lib/constants/notifications";

import { ASSESSMENT_STATUS_TYPES } from "@/constants/assessment";
import { NotificationI } from "@/lib/types/notification";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

interface IconCircleWrapperPropsI
  extends PropsWithChildren, PropsWithClassNameI {
  isGreen?: boolean;
  isYellow?: boolean;
  isRed?: boolean;
  isFire?: boolean;
  isPurpleGradient?: boolean;
}

const IconCircleWrapper: FC<IconCircleWrapperPropsI> = ({
  children,
  className,
  isGreen,
  isYellow,
  isRed,
  isFire,
  isPurpleGradient,
}) => {
  return (
    <div
      className={clsxMerge(
        "h-6 w-6 rounded-full",
        "bg-black text-white",
        "flex items-center justify-center",
        {
          "bg-[#6BC327]": isGreen,
          "bg-[#E99E2E]": isYellow,
          "bg-[#CA3C27]": isRed,
          "bg-[#F44B43]": isFire,
          "bg-gradient-to-r from-[#CB36AD] via-[#7E46CA] to-[#4C6192]":
            isPurpleGradient,
        },
        className
      )}
    >
      {isFire && <span className="absolute top-[-18px] text-[20px]">🔥</span>}
      <span className="z-[1]">{children}</span>
    </div>
  );
};

const NotificationIcon = (props: NotificationI) => {
  const { notification_type, audition_status, meeting_feedback, dial_block } =
    props || {};

  switch (notification_type) {
    case NOTIFICATION_TYPES.CUSTOMER_FORUM_POST:
      return (
        <IconCircleWrapper isGreen>
          <ChatBubbleLeftEllipsisIcon className="w-4" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.EMAIL_OPENED:
      return (
        <IconCircleWrapper isYellow>
          <EnvelopeOpenIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.EMAIL_RECEIVED:
      return (
        <IconCircleWrapper isFire>
          <EnvelopeIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.MISSED_CALL:
      return (
        <IconCircleWrapper isRed>
          <PhoneRejectIcon className="bottom-[-3px] left-[-3px] w-[14px]" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.AUDITION:
      switch (audition_status) {
        case ASSESSMENT_STATUS_TYPES.SUCCESS:
          return (
            <IconCircleWrapper isGreen>
              <HandThumbUpIcon className="w-3" />
            </IconCircleWrapper>
          );
        case ASSESSMENT_STATUS_TYPES.BLOCKED:
          return (
            <IconCircleWrapper isRed>
              <CloseIcon className="w-4" />
            </IconCircleWrapper>
          );
        case ASSESSMENT_STATUS_TYPES.RETRY:
          return (
            <IconCircleWrapper isYellow>
              <ArrowPathIcon className="w-4" />
            </IconCircleWrapper>
          );
      }
      break;
    case NOTIFICATION_TYPES.LIST_RECEIVED:
      return (
        <IconCircleWrapper isPurpleGradient>
          <StarIcon className="w-4" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.MEETING:
      switch (meeting_feedback) {
        case DISPOSITIONS.MEETING_QUALIFIED:
          return (
            <IconCircleWrapper isGreen>
              <HandThumbUpIcon className="w-3" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_NOT_QUALIFIED:
          return (
            <IconCircleWrapper isRed>
              <CloseIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_RESCHEDULED:
          return (
            <IconCircleWrapper isYellow>
              <CalendarIcon className="w-3" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.CANCELLED_MEETING:
          return (
            <IconCircleWrapper isRed>
              <CloseIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_NO_SHOW:
          return (
            <IconCircleWrapper isRed>
              <CloseIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_SHOWN:
          return (
            <IconCircleWrapper isGreen>
              <HandThumbUpIcon className="w-3" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_PAID_OUT:
          return (
            <IconCircleWrapper isGreen>
              <BanknotesIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_DISPUTED:
          return (
            <IconCircleWrapper isYellow>
              <ExclamationTriangleIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_DISPUTE_PAID_OUT:
          return (
            <IconCircleWrapper isGreen>
              <BanknotesIcon className="w-4" />
            </IconCircleWrapper>
          );
        case DISPOSITIONS.MEETING_DISPUTE_NOT_PAID_OUT:
          return (
            <IconCircleWrapper isRed>
              <CloseIcon className="w-4" />
            </IconCircleWrapper>
          );
      }
      break;
    case NOTIFICATION_TYPES.COLD_OUTREACH_EMAIL_RESPONDED:
      return (
        <IconCircleWrapper isPurpleGradient>
          <EnvelopeIcon className="w-4" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.CALENDAR_INVITE_RECEIVED:
      return (
        <IconCircleWrapper isYellow>
          <CalendarIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_APPROVED:
    case NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_DENIED:
      return (
        <IconCircleWrapper
          isGreen={
            notification_type ===
            NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_APPROVED
          }
          isRed={
            notification_type ===
            NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_DENIED
          }
        >
          <CalendarIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.DIAL_BLOCK_CHANGED:
      return (
        <IconCircleWrapper
          isGreen={dial_block?.action === "booked"}
          isRed={dial_block?.action === "cancelled"}
          isYellow={dial_block?.action === "moved"}
        >
          <CalendarIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.DIAL_BLOCK_STRIKE_ALERT:
      return (
        <IconCircleWrapper isRed>
          <ExclamationTriangleIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.DIAL_BLOCK_REQUEST_WAITING:
      return (
        <IconCircleWrapper isYellow>
          <CalendarIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.INCENTIVE_STARTED:
      return (
        <IconCircleWrapper isPurpleGradient>
          <TrophyIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.INCENTIVE_WON:
      return (
        <IconCircleWrapper isGreen>
          <BanknotesIcon className="w-3" />
        </IconCircleWrapper>
      );
    case NOTIFICATION_TYPES.TOP_TEN_DUE:
    case NOTIFICATION_TYPES.TOP_TEN_DIGEST:
      return (
        <IconCircleWrapper isYellow>
          <FireIcon className="w-3" />
        </IconCircleWrapper>
      );
  }

  return null;
};

export const NotificationTypeIcon = (props: NotificationI) => {
  return (
    <div className={clsxMerge("absolute -bottom-1 -right-1")}>
      <NotificationIcon {...props} />
    </div>
  );
};
